/**
 * Built-in merchant dictionary: clean name + default category slug.
 *
 * Ships with the client so matching and default categories work on the device
 * (private mode never sends a narration anywhere). The API seed reads the same
 * list. Categories are per-household, so a default is stored as a slug and
 * resolved to the household's own category id at use time.
 */
import dictionary from "./merchant-dictionary.json"

export interface DefaultMerchant {
  rawId: string
  cleanName: string
  categorySlug: string
  isRecurring?: boolean
}

const builtIn: DefaultMerchant[] = [
  // Food delivery
  { rawId: "ZOMATO", cleanName: "Zomato", categorySlug: "food_delivery" },
  { rawId: "SWIGGY", cleanName: "Swiggy", categorySlug: "food_delivery" },
  { rawId: "BLINKIT", cleanName: "Blinkit", categorySlug: "quick_commerce" },
  { rawId: "ZEPTO", cleanName: "Zepto", categorySlug: "quick_commerce" },
  { rawId: "BIGBASKET", cleanName: "BigBasket", categorySlug: "groceries" },
  { rawId: "DUNZO", cleanName: "Dunzo", categorySlug: "quick_commerce" },

  // Shopping
  { rawId: "AMAZON", cleanName: "Amazon", categorySlug: "amazon_shopping" },
  { rawId: "FLIPKART", cleanName: "Flipkart", categorySlug: "electronics" },
  { rawId: "MYNTRA", cleanName: "Myntra", categorySlug: "clothing" },
  { rawId: "AJIO", cleanName: "Ajio", categorySlug: "clothing" },

  // Transport
  { rawId: "UBER", cleanName: "Uber", categorySlug: "ride_hailing" },
  { rawId: "OLA", cleanName: "Ola", categorySlug: "ride_hailing" },
  { rawId: "RAPIDO", cleanName: "Rapido", categorySlug: "ride_hailing" },

  // Utilities (Bangalore)
  { rawId: "BESCOM", cleanName: "BESCOM", categorySlug: "electricity", isRecurring: true },
  { rawId: "BWSSB", cleanName: "BWSSB", categorySlug: "water", isRecurring: true },
  { rawId: "ACTFIBERNET", cleanName: "ACT Fibernet", categorySlug: "internet", isRecurring: true },
  { rawId: "JIO", cleanName: "Jio", categorySlug: "internet", isRecurring: true },
  { rawId: "AIRTEL", cleanName: "Airtel", categorySlug: "internet", isRecurring: true },

  // Subscriptions
  { rawId: "NETFLIX", cleanName: "Netflix", categorySlug: "subscriptions", isRecurring: true },
  { rawId: "SPOTIFY", cleanName: "Spotify", categorySlug: "subscriptions", isRecurring: true },
  {
    rawId: "PRIMEVIDEO",
    cleanName: "Prime Video",
    categorySlug: "subscriptions",
    isRecurring: true,
  },
  {
    rawId: "YOUTUBE",
    cleanName: "YouTube Premium",
    categorySlug: "subscriptions",
    isRecurring: true,
  },
  { rawId: "HOTSTAR", cleanName: "Hotstar", categorySlug: "subscriptions", isRecurring: true },

  // Fuel
  { rawId: "HPCL", cleanName: "HPCL", categorySlug: "fuel" },
  { rawId: "BPCL", cleanName: "BPCL", categorySlug: "fuel" },
  { rawId: "IOCL", cleanName: "Indian Oil", categorySlug: "fuel" },
]

/**
 * The wider dictionary (#322): `merchant-dictionary.json`, one row per merchant as
 * [clean name, default category slug, narration aliases, recurring?]. Aliases feed the
 * on-device alias table (aliases.ts); every merchant also becomes a global merchant row
 * via a migration, so the rawId is derived from the name and must stay stable.
 */
type DictionaryRow = [cleanName: string, categorySlug: string, aliases: string[], recurring?: 1]

/** Stable global-merchant rawId for a dictionary name: "Domino's" → "DOMINOS". */
export function dictionaryRawId(cleanName: string): string {
  return cleanName.toUpperCase().replace(/[^A-Z0-9]/g, "")
}

export const dictionaryMerchants = (dictionary as DictionaryRow[]).map(
  ([cleanName, categorySlug, aliases, recurring]) => ({
    rawId: dictionaryRawId(cleanName),
    cleanName,
    categorySlug,
    aliases,
    ...(recurring ? { isRecurring: true } : {}),
  }),
)

const builtInNames = new Set(builtIn.map((m) => m.cleanName.toLowerCase()))

export const defaultMerchants: DefaultMerchant[] = [
  ...builtIn,
  ...dictionaryMerchants
    .filter((m) => !builtInNames.has(m.cleanName.toLowerCase()))
    .map(({ aliases: _aliases, ...m }) => m),
]

const slugByName = new Map(defaultMerchants.map((m) => [m.cleanName.toLowerCase(), m.categorySlug]))

/** Default category slug for a built-in merchant, by its clean name. */
export function defaultCategorySlug(cleanName: string): string | undefined {
  return slugByName.get(cleanName.toLowerCase())
}
